import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  InputAdornment,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import api from '../api/api';

const ClientReportsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState({
    total_clients: 0,
    total_revenue: 0,
    total_deals: 0,
    avg_revenue_per_deal: 0
  });
  
  // Фильтры
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [minRevenue, setMinRevenue] = useState('');
  const [maxRevenue, setMaxRevenue] = useState('');
  const [minDeals, setMinDeals] = useState('');
  const [attractionSource, setAttractionSource] = useState('');
  const [sortBy, setSortBy] = useState('revenue');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Пагинация
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchReports();
    fetchSummary();
  }, [page, pageSize, sortBy, sortOrder, refreshVersion]);

  const fetchReports = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = new URLSearchParams();
      
      if (searchQuery) params.append('search', searchQuery);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (minRevenue) params.append('min_revenue', minRevenue);
      if (maxRevenue) params.append('max_revenue', maxRevenue);
      if (minDeals) params.append('min_deals', minDeals);
      if (attractionSource) params.append('attraction_source', attractionSource);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      params.append('page', page);
      params.append('limit', pageSize);

      const response = await api.get(`/reports/clients?${params.toString()}`);
      setClients(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Ошибка загрузки отчета:', error);
      setLoadError('Не удалось загрузить отчёт. Повторите запрос.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const response = await api.get(`/reports/clients/summary?${params.toString()}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Ошибка загрузки сводки:', error);
    }
  };

  const handleApplyFilters = () => {
    setPage(1);
    setRefreshVersion(version => version + 1);
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setStartDate('');
    setEndDate('');
    setMinRevenue('');
    setMaxRevenue('');
    setMinDeals('');
    setAttractionSource('');
    setSortBy('revenue');
    setSortOrder('desc');
    setPage(1);
    setRefreshVersion(version => version + 1);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(value);
  };

  const getSourceColor = (source) => {
    const colors = {
      'Реклама': 'error',
      'Социальные сети': 'info',
      'Рекомендация': 'success',
      'Веб-сайт': 'primary',
      'Прямое обращение': 'warning',
      'Другое': 'default'
    };
    return colors[source] || 'default';
  };

  return (
    <Box className="erp-report-page" sx={{ p: 3 }}>
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {/* Заголовок */}
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        📊 Отчет по клиентам
      </Typography>

      {/* Сводная статистика */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Всего клиентов
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {summary.total_clients}
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Общая выручка
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {formatCurrency(summary.total_revenue)}
                  </Typography>
                </Box>
                <MoneyIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Всего сделок
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {summary.total_deals}
                  </Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 40, color: 'info.main', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Средний чек
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                    {formatCurrency(summary.avg_revenue_per_deal)}
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, color: 'warning.main', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Фильтры */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Поиск"
                placeholder="ФИО, телефон, email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Дата от"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Дата до"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Источник</InputLabel>
                <Select
                  value={attractionSource}
                  onChange={(e) => setAttractionSource(e.target.value)}
                  label="Источник"
                >
                  <MenuItem value="">Все</MenuItem>
                  <MenuItem value="advertising">Реклама</MenuItem>
                  <MenuItem value="social_media">Соц. сети</MenuItem>
                  <MenuItem value="recommendation">Рекомендация</MenuItem>
                  <MenuItem value="website">Веб-сайт</MenuItem>
                  <MenuItem value="direct">Прямое обращение</MenuItem>
                  <MenuItem value="other">Другое</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Сортировка</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Сортировка"
                >
                  <MenuItem value="revenue">По выручке</MenuItem>
                  <MenuItem value="deals_count">По количеству сделок</MenuItem>
                  <MenuItem value="visits_count">По посещениям</MenuItem>
                  <MenuItem value="name">По имени</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Мин. выручка"
                value={minRevenue}
                onChange={(e) => setMinRevenue(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Макс. выручка"
                value={maxRevenue}
                onChange={(e) => setMaxRevenue(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Мин. сделок"
                value={minDeals}
                onChange={(e) => setMinDeals(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Порядок</InputLabel>
                <Select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  label="Порядок"
                >
                  <MenuItem value="desc">По убыванию</MenuItem>
                  <MenuItem value="asc">По возрастанию</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleApplyFilters}
                >
                  Применить
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleResetFilters}
                >
                  Сбросить
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Таблица */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                📋 Список клиентов
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Всего: {total} клиентов
              </Typography>
            </Box>
            
            {/* Выбор количества записей */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Показывать:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(e.target.value);
                    setPage(1);
                  }}
                  sx={{ height: 32 }}
                >
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>ФИО</strong></TableCell>
                    <TableCell><strong>Телефон</strong></TableCell>
                    <TableCell align="center"><strong>Посещения</strong></TableCell>
                    <TableCell align="center"><strong>Аренды</strong></TableCell>
                    <TableCell align="center"><strong>Продажи</strong></TableCell>
                    <TableCell align="center"><strong>Услуги</strong></TableCell>
                    <TableCell><strong>Источник</strong></TableCell>
                    <TableCell align="right"><strong>Выручка</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clients.map((client) => (
                    <TableRow 
                      key={client.id} 
                      hover
                      onClick={() => navigate(`/clients/edit/${client.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography
                          sx={{ 
                            color: 'primary.main',
                            fontWeight: 500,
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {client.full_name}
                        </Typography>
                      </TableCell>
                      <TableCell>{client.phone || '-'}</TableCell>
                      <TableCell align="center">{client.visits_count}</TableCell>
                      <TableCell align="center">{client.deals_count}</TableCell>
                      <TableCell align="center">{client.sales_count}</TableCell>
                      <TableCell align="center">{client.services_count}</TableCell>
                      <TableCell>
                        <Chip
                          label={client.attraction_source_label}
                          color={getSourceColor(client.attraction_source_label)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'bold', color: 'success.main' }}
                        >
                          {formatCurrency(client.total_revenue)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Пагинация */}
          {!loading && total > pageSize && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
              <Button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                variant="outlined"
              >
                Назад
              </Button>
              <Typography sx={{ mx: 2, alignSelf: 'center' }}>
                Страница {page} из {Math.ceil(total / pageSize)}
              </Typography>
              <Button
                disabled={page >= Math.ceil(total / pageSize)}
                onClick={() => setPage(page + 1)}
                variant="outlined"
              >
                Вперед
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClientReportsPage;
